import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlarmClock,
  Archive,
  Flame,
  LayoutGrid,
  List,
  Megaphone,
  Plus,
  Search,
  Tag,
  UserCircle,
} from 'lucide-react';
import {
  useLeadBoard,
  useLeadSourcesInUse,
  useLeadFxRates,
  useLeadPipelines,
  useLeads,
  useUpdateLeadById,
} from '../../hooks/useLeads';
import { useLabels } from '../../hooks/useLabels';
import { usePeople } from '../../hooks/usePeople';
import { usePermissions } from '../../hooks/usePermissions';
import { useI18n } from '../../contexts/I18nContext';
import type { Lead, LeadSource, LeadTemperature } from '../../lib/leadsApi';
import {
  DISPLAY_CURRENCIES,
  SOURCE_OPTIONS,
  TEMPERATURE_OPTIONS,
  type DisplayCurrency,
} from './leadConstants';
import { LeadKanbanBoard, type StageMove } from './LeadKanbanBoard';
import { LeadList } from './LeadList';
import { LeadBulkActionsBar } from './LeadBulkActionsBar';
import { CreateLeadModal } from './CreateLeadModal';
import { PipelineTabs } from './PipelineTabs';
import { ToastProvider, useToast } from '../tasks/linear/Toast';
import { MultiSelect } from '../ui/MultiSelect';
import { MobileFilterSheet, type FilterDescriptor } from '../ui/MobileFilterSheet';
import { usePrompt } from '../ui/ConfirmProvider';

const PAGE_SIZE = 25;
// View mode persists across navigation — mirrors the Tasks view (CLE-197).
const VIEW_KEY = 'cle191:leadsViewMode';

type ViewMode = 'list' | 'board';

// Currency every lead value is shown in on this page (per browser).
const CURRENCY_KEY = 'firmio:leadsDisplayCurrency';

function loadDisplayCurrency(): DisplayCurrency {
  try {
    return localStorage.getItem(CURRENCY_KEY) === 'RON' ? 'RON' : 'EUR';
  } catch {
    return 'EUR';
  }
}

const BUILT_IN_SOURCES = new Set<string>(SOURCE_OPTIONS.map((o) => o.value));

function loadViewMode(): ViewMode {
  try {
    const raw = localStorage.getItem(VIEW_KEY);
    return raw === 'list' ? 'list' : 'board';
  } catch {
    return 'board';
  }
}

/**
 * CLE-191 (spec 3.2) — the Pipeline page: one tab per pipeline (Vânzări,
 * Onboarding, …), filters bar, Board/List toggle, drag-and-drop board and
 * tabular list. The tab and the filters live in the URL so they survive
 * reloads and deep links. Moving a lead to a WON stage of a sales pipeline
 * makes it a client and moves it into onboarding on the server (a toast says
 * so); moving to LOST prompts a reason (recorded in the activity log via the
 * update).
 */
/**
 * Public entry: wraps the view in a ToastProvider so lead-creation toasts
 * (and any future transient feedback) render even though the app has no global
 * toast root yet.
 */
export function LeadsView() {
  return (
    <ToastProvider>
      <LeadsViewInner />
    </ToastProvider>
  );
}

function LeadsViewInner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useI18n();

  const seed = (key: string) => searchParams.get(key);
  // Multi-value filters are repeated params (?label=a&label=b): typed sources
  // are free text and may contain commas. A lone old-style link still reads.
  const seedAll = (key: string) => searchParams.getAll(key).filter(Boolean);

  // --- Filter state -------------------------------------------------------
  const [searchInput, setSearchInput] = useState(() => seed('q') ?? '');
  const [search, setSearch] = useState(() => seed('q') ?? '');
  const [temperatures, setTemperatures] = useState<string[]>(() => seedAll('temperature'));
  // Built-in LeadSources and sources someone typed (filtered via sourceLabel),
  // side by side in one dropdown.
  const [sources, setSources] = useState<string[]>(() => seedAll('source'));
  const [assigneeIds, setAssigneeIds] = useState<string[]>(() => seedAll('assignee'));
  const [labelIds, setLabelIds] = useState<string[]>(() => seedAll('label'));
  const [overdueOnly, setOverdueOnly] = useState(() => seed('overdue') === '1');
  // Archived leads live only in the list: the board shows the live pipeline.
  const [archivedOnly, setArchivedOnly] = useState(() => seed('archived') === '1');
  // The pipeline tab picked (?pipeline=); empty = the default pipeline.
  const [pipelineParam, setPipelineParam] = useState(() => seed('pipeline') ?? '');
  const [page, setPage] = useState(() => {
    const p = Number(seed('page'));
    return Number.isFinite(p) && p > 0 ? p : 1;
  });

  const [viewMode, setView] = useState<ViewMode>(() => loadViewMode());
  const view: ViewMode = archivedOnly ? 'list' : viewMode;
  const [displayCurrency, setDisplayCurrency] = useState<DisplayCurrency>(() =>
    loadDisplayCurrency(),
  );
  const { data: fxRates } = useLeadFxRates();
  const { data: sourcesInUse } = useLeadSourcesInUse();

  useEffect(() => {
    try {
      localStorage.setItem(CURRENCY_KEY, displayCurrency);
    } catch {
      // Non-fatal: persistence is a convenience only.
    }
  }, [displayCurrency]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: pipelines } = useLeadPipelines();
  const { data: labels } = useLabels('leads');
  const { data: people } = usePeople({ active: 'true' });

  // The pipeline shown: the tab picked, else the default one. Board and list
  // always show a single pipeline.
  const pipelineOptions = useMemo(() => pipelines ?? [], [pipelines]);
  const activePipeline =
    pipelineOptions.find((p) => p.id === pipelineParam) ??
    pipelineOptions.find((p) => p.isDefault) ??
    pipelineOptions[0];
  const effectivePipelineId = activePipeline?.id;
  // Onboarding cards come from won leads, not from "+ Adaugă".
  const isOnboarding = activePipeline?.kind === 'ONBOARDING';

  // Persist the view mode so it survives navigation between contexts.
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, viewMode);
    } catch {
      // Non-fatal: persistence is a convenience only.
    }
  }, [viewMode]);

  // Debounce the full-text search input.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Mirror the tab and the filter state into the URL query string.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const set = (key: string, value: string) => {
      if (value) next.set(key, value);
      else next.delete(key);
    };
    const setAll = (key: string, values: string[]) => {
      next.delete(key);
      values.forEach((v) => next.append(key, v));
    };
    set('q', search);
    setAll('temperature', temperatures);
    setAll('source', sources);
    setAll('assignee', assigneeIds);
    setAll('label', labelIds);
    set('overdue', overdueOnly ? '1' : '');
    set('archived', archivedOnly ? '1' : '');
    set('page', page > 1 ? String(page) : '');
    set('pipeline', pipelineParam);
    setSearchParams(next, { replace: true });
  }, [
    search,
    temperatures,
    sources,
    assigneeIds,
    labelIds,
    overdueOnly,
    archivedOnly,
    pipelineParam,
    page,
  ]);

  // Only sources some lead actually has (plus the active ones, so a stale URL
  // filter still shows its value).
  const sourceFilterOptions = useMemo(() => {
    const used = new Set<string>((sourcesInUse?.builtIn ?? []).map((b) => b.source));
    const builtIn = SOURCE_OPTIONS.filter(
      (o) => used.has(o.value) || sources.includes(o.value),
    ).map((o) => ({ value: o.value as string, label: t(`leads.source.${o.value}`) }));
    const typed = (sourcesInUse?.sources ?? [])
      .filter((c) => !BUILT_IN_SOURCES.has(c.label))
      .map((c) => ({ value: c.label, label: c.label }));
    for (const s of sources) {
      if (!BUILT_IN_SOURCES.has(s) && !typed.some((o) => o.value === s)) {
        typed.push({ value: s, label: s });
      }
    }
    return [...builtIn, ...typed];
  }, [sourcesInUse, sources, t]);

  const sharedFilters = {
    temperature: temperatures as LeadTemperature[],
    source: sources.filter((s) => BUILT_IN_SOURCES.has(s)) as LeadSource[],
    sourceLabel: sources.filter((s) => !BUILT_IN_SOURCES.has(s)),
    assigneeId: assigneeIds,
    label: labelIds,
    search: search || undefined,
    hasOverdueActions: overdueOnly || undefined,
  };

  // Board data: grouped by stage for the effective pipeline.
  const board = useLeadBoard({
    pipelineId: effectivePipelineId,
    ...sharedFilters,
  });

  // List data: flat, filtered, paginated.
  const list = useLeads({
    pipelineId: effectivePipelineId,
    ...sharedFilters,
    // Only ever `true`: the server coerces the string "false" to true.
    archived: archivedOnly || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const update = useUpdateLeadById();
  const promptText = usePrompt();
  const { can } = usePermissions();
  const canCreateLeads = can('leads', 'canCreate') && !isOnboarding;

  // Create-lead modal state. `modalStageId` pre-selects a stage when opened
  // from a Kanban column "+"; otherwise the modal resolves smart defaults.
  const [createOpen, setCreateOpen] = useState(false);
  const [modalStageId, setModalStageId] = useState<string | undefined>(undefined);

  const openCreateModal = useCallback((stageId?: string) => {
    setModalStageId(stageId);
    setCreateOpen(true);
  }, []);

  const boardColumns = board.data?.columns ?? [];

  // Soonest incomplete next-action due per lead — not yet surfaced by the
  // board/list serializers, so left empty until that data is wired through.
  const nextActionDueByLead: Record<string, string | null> = useMemo(() => ({}), []);

  const leads = list.data?.leads ?? [];
  const pagination = list.data?.pagination;

  // No dedicated lead detail page exists yet (separate ticket); route to the
  // lead's URL so deep-linking works once that page lands.
  const openLead = useCallback((id: string) => navigate(`/leads/${id}`), [navigate]);

  const lastSelectedRef = useRef<string | null>(null);

  function toggleSelect(id: string, shift = false) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (shift && lastSelectedRef.current) {
        const ids = leads.map((l) => l.id);
        const a = ids.indexOf(lastSelectedRef.current);
        const b = ids.indexOf(id);
        if (a !== -1 && b !== -1) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          for (let i = lo; i <= hi; i++) next.add(ids[i]);
          return next;
        }
      }
      if (next.has(id)) next.delete(id);
      else next.add(id);
      lastSelectedRef.current = id;
      return next;
    });
  }

  // The WON rule ran on the server: the lead became a client and (usually)
  // moved into onboarding. Say so, with a link to the client.
  function announceWon(lead: Lead) {
    const tr = lead.transition;
    if (!tr) return;
    const message = tr.toPipeline
      ? t('leads.won.toast', {
          title: lead.title,
          client: tr.clientName,
          pipeline: tr.toPipeline.name,
        })
      : t('leads.won.toastClientOnly', { title: lead.title, client: tr.clientName });
    toast.show(message, { label: t('leads.won.openClient'), href: `/clients/${tr.clientId}` });
  }

  // Drag-and-drop / stage change handler. WON in a sales pipeline makes the
  // lead a client on the server (no question asked); LOST prompts a loss
  // reason recorded in the activity log via the update.
  async function handleStageMove(move: StageMove) {
    const { leadId, toStage, stageOrder } = move;

    if (toStage.type === 'WON') {
      update.mutate({ id: leadId, stageId: toStage.id, stageOrder }, { onSuccess: announceWon });
      return;
    }

    if (toStage.type === 'LOST') {
      const reason = (await promptText({ message: t('leads.lost.prompt') })) ?? '';
      update.mutate({
        id: leadId,
        stageId: toStage.id,
        stageOrder,
        description: reason ? `${t('leads.lost.prefix')}: ${reason}` : undefined,
      });
      return;
    }

    update.mutate({ id: leadId, stageId: toStage.id, stageOrder });
  }

  // The pipeline tab is not a filter: "Șterge filtrele" leaves it alone.
  const hasFilters = Boolean(
    search ||
    temperatures.length ||
    sources.length ||
    assigneeIds.length ||
    labelIds.length ||
    overdueOnly,
  );

  function resetFilters() {
    setSearchInput('');
    setSearch('');
    setTemperatures([]);
    setSources([]);
    setAssigneeIds([]);
    setLabelIds([]);
    setOverdueOnly(false);
    setPage(1);
  }

  function onFilter<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  function handleLeadCreated(lead: Lead) {
    if (lead.transition) announceWon(lead);
    else toast.show(t('leads.created', { identifier: lead.identifier }));
  }

  function selectPipeline(id: string) {
    setPipelineParam(id);
    setPage(1);
    setSelected(new Set());
  }

  const assignable = (people ?? []).filter((p) => p.userId);
  const labelOptions = useMemo(() => labels ?? [], [labels]);

  const isLoading = view === 'board' ? board.isLoading : list.isLoading;
  const isError = view === 'board' ? board.isError : list.isError;

  const temperatureOptions = TEMPERATURE_OPTIONS.map((o) => ({
    value: o.value as string,
    label: t(`leads.temp.${o.value}`),
  }));
  const assigneeOptions = assignable.map((p) => ({
    value: p.userId as string,
    label: p.fullName,
  }));
  const labelFilterOptions = labelOptions.map((l) => ({
    value: l.id,
    label: l.name,
    color: l.color,
  }));
  const selectedSummary = (count: number) => t('leads.filter.selectedCount', { count });

  const mobileFilters: FilterDescriptor[] = [
    {
      kind: 'multi-select',
      id: 'temperature',
      label: t('leads.filter.temperature'),
      icon: <Flame size={16} />,
      values: temperatures,
      onChange: onFilter(setTemperatures),
      options: temperatureOptions,
      placeholder: t('leads.filter.allTemperatures'),
    },
    {
      kind: 'multi-select',
      id: 'source',
      label: t('leads.filter.source'),
      icon: <Megaphone size={16} />,
      values: sources,
      onChange: onFilter(setSources),
      options: sourceFilterOptions,
      placeholder: t('leads.filter.allSources'),
    },
    {
      kind: 'multi-select',
      id: 'assignee',
      label: t('leads.filter.assignee'),
      icon: <UserCircle size={16} />,
      values: assigneeIds,
      onChange: onFilter(setAssigneeIds),
      options: assigneeOptions,
      placeholder: t('leads.filter.allAssignees'),
    },
    {
      kind: 'multi-select',
      id: 'labels',
      label: t('leads.filter.labels'),
      icon: <Tag size={16} />,
      values: labelIds,
      onChange: onFilter(setLabelIds),
      options: labelFilterOptions,
      placeholder: t('leads.filter.allLabels'),
    },
    {
      // The sheet has no checkbox row; a one-option select reads the same.
      kind: 'select',
      id: 'overdue',
      label: t('leads.filter.overdue'),
      icon: <AlarmClock size={16} />,
      value: overdueOnly ? '1' : '',
      onChange: (v) => onFilter(setOverdueOnly)(v === '1'),
      options: [{ value: '1', label: t('leads.filter.overdueOnly') }],
      placeholder: t('common.all'),
    },
  ];

  return (
    <div className="w-full">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        {/* Phones and tablets (below lg, where the sidebar takes half the width): "+ Adaugă" sits beside the title, the toggles get a row of
            their own below (icons only for Board / Listă); in one row they
            ran off the right edge. */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{t('leads.title')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('leads.subtitle')}</p>
          </div>
          {canCreateLeads && (
            <button
              type="button"
              onClick={() => openCreateModal()}
              className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 lg:hidden"
            >
              <Plus size={16} /> {t('leads.add')}
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Currency every value is shown in; others convert at the BNR rate. */}
          <div
            className="inline-flex rounded border border-gray-300 p-0.5"
            title={fxRates ? `Curs BNR ${fxRates.date}` : undefined}
          >
            {DISPLAY_CURRENCIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setDisplayCurrency(c)}
                aria-pressed={displayCurrency === c}
                className={`rounded px-2.5 py-1.5 text-sm ${
                  displayCurrency === c
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              setArchivedOnly((v) => !v);
              setPage(1);
              setSelected(new Set());
            }}
            aria-pressed={archivedOnly}
            title={t('leads.filter.archivedHint')}
            className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-sm ${
              archivedOnly
                ? 'border-brand-500 bg-brand-500/10 text-brand-600'
                : 'border-gray-300 text-gray-500 hover:text-gray-700'
            }`}
          >
            <Archive size={15} /> {t('leads.filter.archived')}
          </button>
          {/* List / Board view toggle. */}
          <div className="inline-flex rounded border border-gray-300 p-0.5">
            <button
              type="button"
              onClick={() => {
                setArchivedOnly(false);
                setView('board');
              }}
              aria-pressed={view === 'board'}
              aria-label={t('leads.view.board')}
              className={`inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-sm ${
                view === 'board' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <LayoutGrid size={15} />{' '}
              <span className="hidden lg:inline">{t('leads.view.board')}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setArchivedOnly(false);
                setView('list');
              }}
              aria-pressed={view === 'list'}
              aria-label={t('leads.view.list')}
              className={`inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-sm ${
                view === 'list' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <List size={15} /> <span className="hidden lg:inline">{t('leads.view.list')}</span>
            </button>
          </div>
          {canCreateLeads && (
            <button
              type="button"
              onClick={() => openCreateModal()}
              className="hidden items-center justify-center gap-1.5 rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 lg:inline-flex"
            >
              <Plus size={16} /> {t('leads.add')}
            </button>
          )}
        </div>
      </div>

      <div className="mt-4">
        <PipelineTabs
          pipelines={pipelineOptions}
          activeId={effectivePipelineId}
          onChange={selectPipeline}
        />
      </div>

      {/* Phones: search + one Filtre button (bottom sheet), like the other
          list pages, instead of six full-width fields before the first lead. */}
      <div className="mt-4 flex items-center gap-2 lg:hidden">
        <div className="relative min-w-0 flex-1">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('leads.search')}
            aria-label={t('leads.filter.search')}
            className="ui-input min-h-[44px] pl-9"
          />
        </div>
        <MobileFilterSheet filters={mobileFilters} onClearAll={resetFilters} />
      </div>

      <div className="mt-6 hidden flex-wrap items-end gap-3 lg:flex">
        <div className="w-full sm:w-64">
          <label className="ui-field-label">{t('leads.filter.search')}</label>
          <div className="relative">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('leads.search')}
              className="ui-input pl-9"
            />
          </div>
        </div>

        <MultiSelect
          label={t('leads.filter.temperature')}
          value={temperatures}
          onChange={onFilter(setTemperatures)}
          allLabel={t('leads.filter.allTemperatures')}
          summaryLabel={selectedSummary}
          options={temperatureOptions}
          className="w-full sm:w-44"
        />

        <MultiSelect
          label={t('leads.filter.source')}
          value={sources}
          onChange={onFilter(setSources)}
          allLabel={t('leads.filter.allSources')}
          summaryLabel={selectedSummary}
          options={sourceFilterOptions}
          collapseFull={false}
          searchable={sourceFilterOptions.length > 8}
          className="w-full sm:w-44"
        />

        <MultiSelect
          label={t('leads.filter.assignee')}
          value={assigneeIds}
          onChange={onFilter(setAssigneeIds)}
          allLabel={t('leads.filter.allAssignees')}
          summaryLabel={selectedSummary}
          options={assigneeOptions}
          collapseFull={false}
          searchable={assigneeOptions.length > 8}
          className="w-full sm:w-44"
        />

        <MultiSelect
          label={t('leads.filter.labels')}
          value={labelIds}
          onChange={onFilter(setLabelIds)}
          allLabel={t('leads.filter.allLabels')}
          summaryLabel={selectedSummary}
          options={labelFilterOptions}
          collapseFull={false}
          searchable={labelFilterOptions.length > 8}
          className="w-full sm:w-44"
        />

        <label className="inline-flex items-center gap-2 pb-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={(e) => onFilter(setOverdueOnly)(e.target.checked)}
          />
          {t('leads.filter.overdue')}
        </label>

        {hasFilters && (
          <button
            type="button"
            onClick={resetFilters}
            className="pb-2 text-sm text-blue-600 underline"
          >
            {t('leads.filter.clear')}
          </button>
        )}
      </div>

      <div className="mt-6">
        {archivedOnly && (
          <p className="mb-3 flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
            <Archive size={15} className="shrink-0" />
            {t('leads.archivedBanner')}
          </p>
        )}
        {isLoading && <p className="text-gray-500">{t('leads.loading')}</p>}
        {isError && <p className="text-red-600">{t('leads.error')}</p>}

        {!isLoading && !isError && view === 'board' && (
          <>
            {boardColumns.length === 0 ? (
              <p className="text-gray-500">{t('leads.empty.noStages')}</p>
            ) : (
              <LeadKanbanBoard
                columns={boardColumns}
                pipelineId={board.data?.pipeline.id ?? effectivePipelineId ?? ''}
                currency={displayCurrency}
                rates={fxRates}
                nextActionDueByLead={nextActionDueByLead}
                onCardClick={openLead}
                onStageMove={handleStageMove}
                onAddLead={(stageId) => openCreateModal(stageId)}
                canCreate={canCreateLeads}
              />
            )}
          </>
        )}

        {!isLoading && !isError && view === 'list' && (
          <>
            {leads.length === 0 ? (
              <div className="flex flex-col items-start gap-3">
                <p className="text-gray-500">
                  {archivedOnly
                    ? t('leads.empty.archived')
                    : hasFilters
                      ? t('leads.empty.filtered')
                      : t('leads.empty.none')}
                </p>
                {!hasFilters && !archivedOnly && canCreateLeads && (
                  <button
                    type="button"
                    onClick={() => openCreateModal()}
                    className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
                  >
                    <Plus size={16} /> {t('leads.new')}
                  </button>
                )}
              </div>
            ) : (
              <LeadList
                leads={leads}
                selected={selected}
                onToggleSelect={toggleSelect}
                onOpen={openLead}
                onLabelClick={(l) => onFilter(setLabelIds)([l.id])}
                nextActionDueByLead={nextActionDueByLead}
                displayCurrency={displayCurrency}
                rates={fxRates}
              />
            )}

            {pagination && pagination.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <span>
                  {t('leads.pagination', {
                    page: pagination.page,
                    totalPages: pagination.totalPages,
                    total: pagination.total,
                  })}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={pagination.page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    {t('common.previous')}
                  </button>
                  <button
                    type="button"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bulk actions toolbar — only meaningful in list view. */}
      {view === 'list' && !archivedOnly && (
        <LeadBulkActionsBar selectedIds={[...selected]} onClear={() => setSelected(new Set())} />
      )}

      <CreateLeadModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        pipelineId={effectivePipelineId}
        stageId={modalStageId}
        onCreated={handleLeadCreated}
      />
    </div>
  );
}
